import { useEffect, useState } from "react";
import HrefWrapper from "../../Components/HrefWrapper";
import { HasSeenSpeciesCountResponse } from "../../Types/apiResponses";
import { useTableSort } from "../../Utils/generalUtils";

import useLocalStorageValue from "../../Hooks/useLocalStorageValue";
import "./styles.scss";

interface PinnarListSpeciesDetailsProps {
  listSpeciesDetails: Array<HasSeenSpeciesCountResponse>;
  filter: string;
  listId: number;
}

function PinnarListSpeciesDetails({
  listSpeciesDetails,
  filter,
  listId,
}: PinnarListSpeciesDetailsProps) {
  const [language, setLanguage] = useState<
    | "ioc_finnish"
    | "ioc_english"
    | "ioc_scientific"
    | "scientific_abbreviation"
  >("ioc_finnish");

  const [speciesFilter, setSpeciesFilter] = useState<"all" | "seen" | "unseen">("all");

  const [defaultLanguage, setDefaultLanguage] =
    useLocalStorageValue<
      | "ioc_finnish"
      | "ioc_english"
      | "ioc_scientific"
      | "scientific_abbreviation"
      | null
    >("editor-default-language", null);

  useEffect(() => {
    if (defaultLanguage) {
      setLanguage(defaultLanguage);
    }
  }, []);

  const filterNames = () => {
    if (!Array.isArray(listSpeciesDetails)) {
      return [];
    }

    let filteredList = [...listSpeciesDetails];

    if (filter && filter.length > 0) {
      filteredList = filteredList.filter(
        (species: HasSeenSpeciesCountResponse) => {
          if (!species) return false;

          if (species[language]) {
            if (language === "scientific_abbreviation") {
              return Array.isArray(species[language]) && species[language].some((sci: string) =>
                typeof sci === "string" && sci.toLowerCase().includes(filter.toLowerCase())
              );
            } else {
              return typeof species[language] === "string" && species[language]
                .toLowerCase()
                .includes(filter.toLowerCase());
            }
          }
          return false;
        }
      );
    }

    if (speciesFilter === "seen") {
      filteredList = filteredList.filter((species: HasSeenSpeciesCountResponse) =>
        species && species.user_has_seen === true
      );
    } else if (speciesFilter === "unseen") {
      filteredList = filteredList.filter((species: HasSeenSpeciesCountResponse) =>
        species && species.user_has_seen === false
      );
    }

    return filteredList;
  };

  const safeSpeciesDetails = Array.isArray(listSpeciesDetails) ? listSpeciesDetails : [];
  const [order, setOrderKey, setList] = useTableSort(safeSpeciesDetails);

  useEffect(() => {
    const filteredList = filterNames();
    setList(filteredList);
  }, [listSpeciesDetails, speciesFilter, filter, language]);

  const getSeenObservationsHref = (
    speciesId: number,
    listId: number
  ) => {
    return `/havainneet?speciesId=${speciesId}&listId=${listId}`;
  };

  const userObservationsHref = (userId: number) => {
    return `/havainnot?userId=${userId}&listId=${listId}`;
  };

  const changeLanguage = (
    language:
      | "ioc_finnish"
      | "ioc_english"
      | "ioc_scientific"
      | "scientific_abbreviation"
  ) => {
    setLanguage(language);
    setDefaultLanguage(language);
  };

  return (
    <div className="row w-100 m-0 p-0 p-0 justify-content-center">
      <div className="w-100 m-0 p-0 pt-1 pb-3">
        <div className="d-flex flex-wrap justify-content-start mb-2 px-2">
          <button
            className="language-button rounded cursor-pointer mx-1 my-1 py-1 px-2 border-0"
            onClick={() => changeLanguage("ioc_finnish")}
            style={{
              backgroundColor:
                language === "ioc_finnish" ? "#ffbe00" : "#a07702",
            }}
          >
            FI
          </button>
          <button
            className="language-button rounded cursor-pointer mx-1 my-1 py-1 px-2 border-0"
            onClick={() => changeLanguage("ioc_english")}
            style={{
              backgroundColor:
                language === "ioc_english" ? "#ffbe00" : "#a07702",
            }}
          >
            EN
          </button>
          <button
            className="language-button rounded cursor-pointer mx-1 my-1 py-1 px-2 border-0"
            onClick={() => changeLanguage("ioc_scientific")}
            style={{
              backgroundColor:
                language === "ioc_scientific"
                  ? "#ffbe00"
                  : "#a07702",
            }}
          >
            SCI
          </button>
          <button
            className="language-button rounded cursor-pointer mx-1 my-1 py-1 px-2 border-0"
            onClick={() =>
              changeLanguage("scientific_abbreviation")
            }
            style={{
              backgroundColor:
                language === "scientific_abbreviation"
                  ? "#ffbe00"
                  : "#a07702",
            }}
          >
            3+3
          </button>
        </div>
        
        <div className="d-flex flex-wrap justify-content-start px-2">
          <button
            className="language-button rounded cursor-pointer mx-1 my-1 py-1 px-2 border-0"
            onClick={() => setSpeciesFilter("all")}
            style={{
              backgroundColor: speciesFilter === "all" ? "#ffbe00" : "#a07702",
            }}
          >
            Kaikki
          </button>
          <button
            className="language-button rounded cursor-pointer mx-1 my-1 py-1 px-2 border-0"
            onClick={() => setSpeciesFilter("seen")}
            style={{
              backgroundColor: speciesFilter === "seen" ? "#ffbe00" : "#a07702",
            }}
          >
            <span style={{ color: "#ffffff" }}>✓</span>
          </button>
          <button
            className="language-button rounded cursor-pointer mx-1 my-1 py-1 px-2 border-0"
            onClick={() => setSpeciesFilter("unseen")}
            style={{
              backgroundColor: speciesFilter === "unseen" ? "#ffbe00" : "#a07702",
            }}
          >
            <span style={{ color: "#ffffff" }}>✗</span>
          </button>
        </div>
      </div>
      <div className="table-responsive m-0 p-0">
        <table className="table table-striped table-sm">
          <thead className="thead-dark">
            <tr>
              <th
                scope="col"
                className="pinna-list-species-row-number"
              >
                #
              </th>

              <th
                scope="col"
                className="table-column-pressable"
                onClick={() => setOrderKey("systord")}
              >
                Laji
              </th>
              <th
                scope="col"
                className="table-column-pressable"
                onClick={() => setOrderKey("count")}
              >
                Havainneita
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(order) && order.length > 0 ?
              order.map(
                (
                  species: HasSeenSpeciesCountResponse,
                  index: number
                ) => {
                  if (!species || typeof species !== "object") {
                    return null;
                  }

                  return (
                    <tr key={index}>
                      <th
                        scope="row"
                        className="pinna-list-species-row-number"
                      >{`${index + 1}.`}</th>
                      <td className="table-column-pressable" style={species.user_has_seen ? {fontWeight: "bolder"} : {}}>
                        <HrefWrapper
                          href={getSeenObservationsHref(
                            species.species_id || 0,
                            listId
                          )}
                        >
                            {species.user_has_seen ?
                            <span style={{color: "green", fontWeight: "bolder", display: "inline-block", width: "20px", textAlign: "center"}}>✓</span> :
                            <span style={{color: "red", fontWeight: "bolder", display: "inline-block", width: "20px", textAlign: "center"}}>✗</span>
                            }
                            { language === "scientific_abbreviation" ?
                            (Array.isArray(species.scientific_abbreviation) && species.scientific_abbreviation[0] ?
                              species.scientific_abbreviation[0] :
                              (species["ioc_english"] || "Unknown")) :
                            (species[language] || species["ioc_english"] || "Unknown")
                            }
                            {
                            language === "scientific_abbreviation" &&
                            Array.isArray(species.scientific_abbreviation) &&
                            species.scientific_abbreviation[1] ?
                            <span className="secondary-abbr">{species.scientific_abbreviation[1]}</span> :
                            null
                            }
                        </HrefWrapper>
                      </td>
                      {
                        species.have_seen === 1 && species.ace_firstname && species.ace_lastname && species.ace_user_id ?
                        <td className="table-column-pressable">
                          <HrefWrapper
                          href={userObservationsHref(
                            species.ace_user_id
                          )}
                          >
                          {`${species.ace_firstname} ${species.ace_lastname}`}
                          </HrefWrapper>
                        </td>
                          :
                        <td>{`${species.have_seen || 0}`}</td>
                      }
                    </tr>
                  );
                }
              )
            :
              <tr>
                <td colSpan={3} className="text-center">
                  Ei lajeja saatavilla...
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PinnarListSpeciesDetails;
